import {
  ComparisonOperators,
  escapeRegExp,
  type LogicalOperators,
} from "@validereinc/utilities";
import { useCallback, useEffect, useRef, useState } from "react";
import { extractDeepestChildTextFromElement } from "../dom";

/**
 * A hook that enables searching against a list of any DOM elements by their
 * attributes in a single container. Mutates the style and attributes of the DOM
 * elements.
 *
 * @returns the containerRef, the onContainerRefChange function, and the current
 * counts (total, totalHidden)
 */
export const useSearchDOM = (
  {
    searchQuery = "",
    containerQuery = "",
    searchByQueries = [],
    match = "$or",
    operator = ComparisonOperators.like,
    enabled = true,
  }: {
    /** The current search query string */
    searchQuery: string;
    /** The query to use to find the containers to search in */
    containerQuery?: string;
    /** The queries to use to search the containers. You can search by attribute, the entire element, and/or element-text. */
    searchByQueries: Array<
      | { type: "attribute"; query: string }
      | { type: "element"; query: string }
      | { type: "element-text"; query: string }
    >;
    /** The logical operator to use when matching queries */
    match?: typeof LogicalOperators.and | typeof LogicalOperators.or;
    /** The comparison operator to use when matching queries */
    operator?:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like;
    /** search will only work when enabled. when this changes, the search containers will be recalculated. */
    enabled?: boolean;
  } = {
    searchQuery: "",
    containerQuery: "",
    searchByQueries: [],
    match: "$or",
    operator: ComparisonOperators.like,
    enabled: true,
  }
) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement>();
  // keep track of containers if containerQuery of type 'multiple'
  const [searchRootContainers, setSearchRootContainers] = useState<Element[]>(
    []
  );
  // keep track of hidden children in a ref, since we don't want to re-render the component when the hidden children change, but only when the count changes
  const hiddenChildren = useRef<HTMLElement[]>([]);
  const [hiddenChildrenCount, setHiddenChildrenCount] = useState(0);

  const onContainerRefChange = useCallback(
    (node: HTMLDivElement) => {
      setContainerRef(node);

      // if the container query is empty, set the root containers to just the ref node
      if (!containerQuery && node) {
        setSearchRootContainers([node]);
        return;
      }

      // re-compute containers
      const containers = node?.querySelectorAll(containerQuery) ?? [];

      setSearchRootContainers(Array.from(containers));
    },
    // re-set ref when enabled or container query changes
    [containerQuery, enabled]
  );

  const searchByAttribute = (
    element: HTMLElement,
    attributeName: string,
    attributeValue: string,
    operator:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like = ComparisonOperators.like
  ): boolean => {
    return !!(element.innerHTML || element.outerHTML)?.match(
      operator === "$like"
        ? new RegExp(`${attributeName}="[^"]*${attributeValue}[^"]*"`, "ig")
        : new RegExp(`${attributeName}="${attributeValue}"`, "ig")
    );
  };

  const searchByElement = (
    element: HTMLElement,
    nodeQuery: string,
    searchQuery: string,
    operator:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like = ComparisonOperators.like
  ): boolean => {
    const queriedElement = element.querySelector(nodeQuery);

    return !!queriedElement?.innerHTML?.match(
      operator === "$like"
        ? new RegExp(searchQuery, "ig")
        : new RegExp(`^(${searchQuery})$`, "ig")
    );
  };

  const searchByElementText = (
    element: HTMLElement,
    nodeQuery: string,
    searchQuery: string,
    operator:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like = ComparisonOperators.like
  ): boolean => {
    const queriedElement = nodeQuery
      ? element.querySelector(nodeQuery)
      : element;

    return !!extractDeepestChildTextFromElement(queriedElement).match(
      operator === "$like"
        ? new RegExp(searchQuery, "ig")
        : new RegExp(`^(${searchQuery})$`, "i")
    );
  };

  const searchWithQuery = (
    element: HTMLElement,
    nodeQuery:
      | { type: "attribute"; query: string }
      | { type: "element"; query: string }
      | { type: "element-text"; query: string },
    searchQuery: string,
    operator:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like = ComparisonOperators.like
  ): boolean => {
    switch (nodeQuery.type) {
      case "attribute":
        return searchByAttribute(
          element,
          nodeQuery.query,
          searchQuery,
          operator
        );
      case "element":
        return searchByElement(element, nodeQuery.query, searchQuery, operator);
      case "element-text":
        return searchByElementText(
          element,
          nodeQuery.query,
          searchQuery,
          operator
        );
    }
  };

  const runSearchInRootContainer = (
    rootContainer: Element | null,
    searchQuery: string,
    operator:
      | typeof ComparisonOperators.exact
      | typeof ComparisonOperators.like = ComparisonOperators.like
  ) => {
    // when there are no children to filter, don't search
    if (!rootContainer?.hasChildNodes() || rootContainer.childNodes.length < 1)
      return;

    for (const child of rootContainer?.children ?? []) {
      // if the child is not an element, skip it
      if (child.nodeType !== child.ELEMENT_NODE) continue;

      // cast forcefully
      const element = child as HTMLElement;
      const isAMatch =
        match === "$or"
          ? searchByQueries.some((query) => {
              return searchWithQuery(element, query, searchQuery, operator);
            })
          : searchByQueries.every((query) => {
              return searchWithQuery(element, query, searchQuery, operator);
            });

      // when attributes or elements match, remove them from the hidden list and reveal them immediately
      if (isAMatch) {
        // Check if this element was previously hidden and restore its display
        const wasHidden = hiddenChildren.current.find(
          (el: HTMLElement) => el === element
        );

        if (wasHidden) {
          // Restore the element's original display style
          if (element.getAttribute("data-og-display")) {
            element.style.display =
              element.getAttribute("data-og-display") ?? "block";
          } else {
            try {
              element.attributeStyleMap?.delete("display");
            } catch {
              // Fallback for browsers that don't support attributeStyleMap
              element.style.display = "";
            }
          }
        }

        // Remove the element from the hidden list
        hiddenChildren.current = hiddenChildren.current.filter(
          (el: HTMLElement) => el !== element
        );
      } else {
        // when attributes or elements don't match, hide the element
        if (
          !hiddenChildren.current.find((item: HTMLElement) => item === element)
        ) {
          hiddenChildren.current.push(element);
          if (element.style.display)
            element.setAttribute("data-og-display", element.style.display);
          element.style.display = "none";
        }
      }
    }
  };

  // this effect is responsible for hiding and showing the children of the containerRef, when the search query changes
  useEffect(() => {
    // if not enabled or if there are no root containers to search in o when the search query hasn't changed, don't run the filtering algo
    if (!enabled || !searchRootContainers.length) return;

    // if the search query is empty, show everything, and don't run the filtering algo
    if (!searchQuery.trim()) {
      // when there are hidden children, show them
      if (hiddenChildren.current.length > 0) {
        hiddenChildren.current.forEach((el) => {
          if (el.getAttribute("data-og-display")) {
            el.style.display = el.getAttribute("data-og-display") ?? "block";
          } else {
            try {
              el.attributeStyleMap?.delete("display");
            } catch {
              // Fallback for browsers that don't support attributeStyleMap
              el.style.display = "";
            }
          }
        });
      }

      hiddenChildren.current = [];
      setHiddenChildrenCount(0);
      return;
    }

    const searchQuerySubject = escapeRegExp(
      operator === "$like" ? searchQuery.toLowerCase() : searchQuery
    );

    for (const rootContainer of searchRootContainers) {
      runSearchInRootContainer(rootContainer, searchQuerySubject, operator);
    }

    // update counts to re-render, since hidden children is a ref (which is intentional)
    setHiddenChildrenCount(hiddenChildren.current.length);
  }, [
    containerRef,
    searchRootContainers,
    searchByQueries,
    match,
    operator,
    searchQuery,
    enabled,
  ]);

  return {
    /** ref of the container holding the DOM elements to search */
    containerRef,
    /** set this function as the ref callback on the container holding the DOM elements to search */
    onContainerRefChange,
    /** total number of searchable DOM elements */
    total: searchRootContainers
      .map((container) => container?.childNodes?.length ?? 0)
      .reduce((acc, curr) => acc + curr, 0),
    /** total number of hidden DOM elements after search applied */
    totalHidden: hiddenChildrenCount,
  };
};
